import { useCallback, useMemo, useRef, useState } from 'react';
import { FIELD_MAP, PLOTTED_FIELDS, HOURLY_FIELDS } from '../lib/fieldMap';
import { parseWecoTimestamp } from '../lib/timestamp';
import { useClock } from './useClock';

export const HISTORY_WINDOW_MS = 60_000;
export const HOURLY_WINDOW_MS = 3_600_000;

// Width of one bucket in the hourly buffer. At ~10Hz an hour of raw samples
// is 36k points; per-second sums are 3.6k, and the mean over them
// (sum of sums / sum of counts) is still exact, not an average of averages.
const HOURLY_BUCKET_MS = 1_000;

// Where a sensor field is read from, in order of preference. The controller
// speaks for itself; the bridge repeats it from the Pi when it cannot.
const SENSOR_SOURCES = ['denshi', 'bridge'];

/**
 * When a message was taken, by the device's clock if it carried a parseable
 * timestamp, else when it arrived here. mqtt-to-bigquery republishes denshi
 * records with `timestamp: null` until it has a clock pairing for the device,
 * and "when we last heard" is far more useful on screen than a blank.
 *
 * @returns {{ at: Date, source: 'device' | 'arrival' } | null}
 */
function slotTime(slot) {
  if (!slot) return null;

  const device = parseWecoTimestamp(slot.timestamp);
  if (device) return { at: device, source: 'device' };
  if (slot._arrivedAtMs != null) return { at: new Date(slot._arrivedAtMs), source: 'arrival' };

  return null;
}

// Shorter than the plot window on purpose: a data-rate readout should react
// when the stream slows, and a 60s window would take a minute to show it.
const RATE_WINDOW_MS = 10_000;

/**
 * Feeds off the onMessage(topic, payload) shape useMqtt produces. Rebuilds the
 * newest-per-sensor message tree using the same rule proven in
 * weco-dashboard's Home.js (array-normalize the payload, sensor_id fallback,
 * drop a point older than what's already stored), then projects FIELD_MAP over
 * that tree into per-field {value, unit, receivedAt, ageMs, stale,
 * everReceived} records that re-derive age/staleness on every clock tick --
 * not on message arrival -- so a field visibly degrades even with zero new
 * traffic.
 *
 * `sources` maps a source name to its wire hostname ({ denshi, bridge, pi });
 * the raw tree is kept per source so one device's `status` never overwrites
 * another's. A sensor field is read from the controller while that is fresh
 * and from the bridge otherwise (SENSOR_SOURCES, in that order); each record
 * says which one it came from in `via`. The series, rate and hourly buffers
 * take the bridge's samples only while the controller's own stream for that
 * message type is stale, so the two roads never double up in a plot.
 *
 * PLOTTED_FIELDS additionally accumulate a rolling HISTORY_WINDOW_MS series in
 * a ref. It's a ref rather than state because the clock tick already re-renders
 * at 4Hz; buffering in state would re-render once per message instead, which
 * for a 10Hz odrive topic is pure waste.
 *
 * HOURLY_FIELDS accumulate a coarser HOURLY_WINDOW_MS buffer of per-second
 * bucket totals the same way, for the rolling hourly average. It fills from
 * page load only -- nothing here queries BigQuery -- so the average is over
 * whatever span has actually been observed, and that span is reported
 * alongside the mean so it can be labelled honestly.
 */
export function useDenshiState(sources) {
  const [rawMessages, setRawMessages] = useState({});
  const hostnameToSource = useMemo(
    () => Object.fromEntries(Object.entries(sources).map(([source, host]) => [host, source])),
    [sources]
  );
  const historyRef = useRef({});
  const hourlyRef = useRef({});
  // Newest device time seen per message type straight from the controller,
  // for deciding whether a bridged sample belongs in the buffers.
  const lastDirectRef = useRef({});
  // Last non-null value seen per field, with when it arrived. GPS in
  // particular drops latitude/longitude entirely while it has no fix, and the
  // last known position is more useful on screen than a blank -- as long as
  // its age is shown alongside, which is what `at` is for.
  const lastGoodRef = useRef({});
  // Arrival times per message type, for the data-rate readout.
  const rateRef = useRef({});
  const nowMs = useClock(250);

  const onMessage = useCallback(
    (topic, data) => {
      const [, msgHostname, messageType, sensorId] = topic.split('/');
      const source = hostnameToSource[msgHostname];
      if (!source) return;

      const points = Array.isArray(data) ? data : data == null ? [] : [data];
      const arrivedAtMs = Date.now();

      for (const dataPoint of points) {
        const pointTs = parseWecoTimestamp(dataPoint.timestamp);
        const pointT = pointTs ? pointTs.getTime() : arrivedAtMs;

        // Whether this sample feeds the buffers. The controller's own always
        // do. The bridge's do only while the controller has been quiet on this
        // message type for longer than the type's staleness -- the same rule
        // `fields` applies -- so a plot never carries both copies of a sample.
        let feedsBuffers = source === 'denshi';
        if (source === 'denshi') {
          lastDirectRef.current[messageType] = pointT;
        } else if (source === 'bridge') {
          const staleAfterMs = Math.min(
            ...Object.values(FIELD_MAP)
              .filter((d) => !d.source && d.messageType === messageType && d.staleAfterMs != null)
              .map((d) => d.staleAfterMs)
          );
          const lastDirect = lastDirectRef.current[messageType];
          feedsBuffers = lastDirect == null || arrivedAtMs - lastDirect > staleAfterMs;
        }

        // Counted per data point, not per MQTT message: a batched payload
        // carries several samples, and it's the sample rate that matters.
        // Device timestamps rather than arrival time, so network jitter and
        // batching don't distort the figure.
        if (feedsBuffers) {
          const ts = parseWecoTimestamp(dataPoint.timestamp);
          const t = ts ? ts.getTime() : Date.now();
          const times = rateRef.current[messageType] || (rateRef.current[messageType] = []);

          times.push(t);
          const cutoff = t - RATE_WINDOW_MS;
          while (times.length && times[0] < cutoff) times.shift();
        }

        for (const key of PLOTTED_FIELDS) {
          const def = FIELD_MAP[key];
          if (!feedsBuffers || def.messageType !== messageType) continue;

          const value = def.path(dataPoint);
          if (value == null || Number.isNaN(value)) continue;

          const ts = parseWecoTimestamp(dataPoint.timestamp);
          const t = ts ? ts.getTime() : Date.now();
          const series = historyRef.current[key] || (historyRef.current[key] = []);

          series.push({ t, v: value });

          const cutoff = t - HISTORY_WINDOW_MS;
          while (series.length && series[0].t < cutoff) series.shift();
        }

        for (const key of HOURLY_FIELDS) {
          const def = FIELD_MAP[key];
          if (!feedsBuffers || def.messageType !== messageType) continue;

          const value = def.path(dataPoint);
          if (value == null || Number.isNaN(value)) continue;

          const ts = parseWecoTimestamp(dataPoint.timestamp);
          const t = Math.floor((ts ? ts.getTime() : Date.now()) / HOURLY_BUCKET_MS) * HOURLY_BUCKET_MS;
          const buckets = hourlyRef.current[key] || (hourlyRef.current[key] = []);
          const last = buckets[buckets.length - 1];

          // Only the newest bucket is ever extended. A sample that arrives out
          // of order simply opens a new bucket; the mean is a sum over all of
          // them, so it stays exact either way.
          if (last && last.t === t) {
            last.sum += value;
            last.n += 1;
          } else {
            buckets.push({ t, sum: value, n: 1 });
          }

          const cutoff = t - HOURLY_WINDOW_MS;
          while (buckets.length && buckets[0].t < cutoff) buckets.shift();
        }

        setRawMessages((prev) => {
          const sensor = sensorId || dataPoint.sensor_id || messageType;
          const tree = prev[source] || {};
          const existing = tree[messageType]?.[sensor];

          if (dataPoint.timestamp && existing?.timestamp) {
            const newTs = parseWecoTimestamp(dataPoint.timestamp);
            const oldTs = parseWecoTimestamp(existing.timestamp);
            if (newTs && oldTs && newTs.getTime() < oldTs.getTime()) {
              return prev;
            }
          }

          return {
            ...prev,
            [source]: {
              ...tree,
              [messageType]: {
                ...tree[messageType],
                // Arrival time rides along so slotTime() can fall back to it.
                [sensor]: { ...dataPoint, _arrivedAtMs: arrivedAtMs },
              },
            },
          };
        });
      }
    },
    [hostnameToSource]
  );

  const fields = useMemo(() => {
    const result = {};

    for (const [key, def] of Object.entries(FIELD_MAP)) {
      // One candidate per source that has ever sent this message type.
      const candidates = [];
      for (const source of def.source ? [def.source] : SENSOR_SOURCES) {
        const slot = rawMessages[source]?.[def.messageType]?.[def.messageType];
        if (slot === undefined) continue;

        const raw = def.path(slot);
        const time = slotTime(slot);
        const ageMs = time ? nowMs - time.at.getTime() : null;

        candidates.push({
          source,
          value: raw === undefined ? null : raw,
          time,
          ageMs,
          stale: def.staleAfterMs != null && ageMs != null ? ageMs > def.staleAfterMs : false,
        });
      }

      // The first fresh source in order of preference wins, even if it lacks
      // the field (a fresh GPS message without a fix is still the truth).
      // With nothing fresh, whichever spoke last -- shown stale.
      const chosen =
        candidates.find((c) => !c.stale) ??
        candidates.reduce(
          (best, c) => (!best || (c.time?.at.getTime() ?? 0) > (best.time?.at.getTime() ?? 0) ? c : best),
          null
        );

      const everReceived = candidates.length > 0;
      const value = chosen ? chosen.value : null;
      const receivedAt = chosen?.time ? chosen.time.at : null;
      const ageMs = chosen ? chosen.ageMs : null;
      const stale = chosen ? chosen.stale : false;

      if (value != null && receivedAt) {
        lastGoodRef.current[key] = { value, at: receivedAt };
      }

      const lastGood = lastGoodRef.current[key] ?? null;

      result[key] = {
        value,
        unit: def.unit,
        receivedAt,
        // 'device' when receivedAt is the device's own clock, 'arrival' when
        // it is only when the message got here.
        timeSource: chosen?.time ? chosen.time.source : null,
        // Which device this reading came from: 'denshi' straight from the
        // controller, 'bridge' repeated by the Pi, 'pi' the Pi's own.
        via: chosen ? chosen.source : null,
        ageMs,
        stale,
        everReceived,
        // Survives a message that arrives without this field (no GPS fix).
        lastValue: lastGood ? lastGood.value : null,
        lastValueAgeMs: lastGood ? nowMs - lastGood.at.getTime() : null,
      };
    }

    return result;
  }, [rawMessages, nowMs]);

  // Rebuilt on the clock tick so the plots scroll smoothly and old points
  // drop off the left edge even while no new messages are arriving.
  const history = useMemo(() => {
    const cutoff = nowMs - HISTORY_WINDOW_MS;
    const result = {};

    for (const key of PLOTTED_FIELDS) {
      const series = historyRef.current[key] || [];
      result[key] = series.filter((p) => p.t >= cutoff);
    }

    return result;
  }, [nowMs]);

  // Rolling mean over the hourly buffer, and how much of the hour it actually
  // spans. Gaps inside that span (a reconnect, a stalled stream) are simply
  // missing buckets: the mean is over the samples received. Rebuilt once a
  // second, not on every 4Hz tick -- it can't change faster than its buckets.
  const nowSecond = Math.floor(nowMs / HOURLY_BUCKET_MS);
  const hourly = useMemo(() => {
    const now = nowSecond * HOURLY_BUCKET_MS;
    const cutoff = now - HOURLY_WINDOW_MS;
    const result = {};

    for (const key of HOURLY_FIELDS) {
      const buckets = (hourlyRef.current[key] || []).filter((b) => b.t >= cutoff);

      if (buckets.length === 0) {
        result[key] = { mean: null, coverageMs: 0 };
        continue;
      }

      let sum = 0;
      let n = 0;
      for (const b of buckets) {
        sum += b.sum;
        n += b.n;
      }

      result[key] = {
        mean: sum / n,
        coverageMs: Math.min(now + HOURLY_BUCKET_MS - buckets[0].t, HOURLY_WINDOW_MS),
      };
    }

    return result;
  }, [nowSecond]);

  // Sample rate per message type, in Hz. Derived from the intervals between
  // samples -- (n-1)/span, not n/window -- so it doesn't read low simply
  // because the window isn't full yet.
  const rates = useMemo(() => {
    const cutoff = nowMs - RATE_WINDOW_MS;
    const result = {};

    for (const [messageType, times] of Object.entries(rateRef.current)) {
      const fresh = times.filter((t) => t >= cutoff);

      if (fresh.length < 2) {
        result[messageType] = null;
        continue;
      }

      const spanSeconds = (fresh[fresh.length - 1] - fresh[0]) / 1000;
      result[messageType] = spanSeconds > 0 ? (fresh.length - 1) / spanSeconds : null;
    }

    return result;
  }, [nowMs]);

  // Newest message per source and across all of them, and whether that time
  // is the device's clock or merely arrival here -- the header shows the two
  // differently. `lastHeard` is per source so the overlay can say how long
  // the controller has been quiet while the Pi is still talking.
  const { latestReceivedAt, latestTimeSource, lastHeard } = useMemo(() => {
    let latest = null;
    const perSource = {};

    for (const source of Object.keys(rawMessages)) {
      let newest = null;

      for (const messageType of Object.keys(rawMessages[source])) {
        for (const sensor of Object.keys(rawMessages[source][messageType])) {
          const time = slotTime(rawMessages[source][messageType][sensor]);
          if (time && (!newest || time.at.getTime() > newest.at.getTime())) newest = time;
        }
      }

      perSource[source] = newest ? newest.at : null;
      if (newest && (!latest || newest.at.getTime() > latest.at.getTime())) latest = newest;
    }

    return {
      latestReceivedAt: latest ? latest.at : null,
      latestTimeSource: latest ? latest.source : null,
      lastHeard: perSource,
    };
  }, [rawMessages]);

  return {
    onMessage,
    fields,
    history,
    hourly,
    rates,
    latestReceivedAt,
    latestTimeSource,
    lastHeard,
    nowMs,
  };
}
